import ActionTextIcon from "@/components/icons/ActionTextIcon";
import AntIcon from "@/components/icons/AntIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleCurrency, formatLocaleDate } from "@/utils/formatUtils";
import { useUpdateFilterSearchParams } from "@/utils/hooksUtils";
import { getTableSorting, sortableColumnProps } from "@/utils/tableUtils";
import { isDefined, numberOrZero } from "@/utils/utils";
import { Button, Card, Col, Divider, Popconfirm, Row, Space, Table } from "antd";
import { ColumnsType } from "antd/es/table";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Link } from "react-router";
import {
  useDownloadPostponedCommissionsExport,
  useFilterPostponedCommissions,
  useResolvePostponedCommission,
  useUnresolvePostponedCommission,
} from "../../../../../common/api/queries";
import { rowGutter, TableSizes } from "../../../../../common/constants";
import { ExportFileType } from "../../../../../common/enums";
import { Permission } from "../../../../../common/security/enums";
import { useHasAnyPermission, useHasPermissions } from "../../../../auth/hooks";
import { CONTRACT_ROUTE_PATHS } from "../../../../contract/paths";
import { CommissionKindTag } from "../../../batch/components/CommissionKindTag";
import { CommissionForm } from "../../../batch/components/forms/CommissionForm";
import PostponementReasonTag from "../../../batch/components/PostponementReasonTag";
import { COMMISSIONS_BATCH_ROUTE_PATHS } from "../../../batch/paths";
import { Commission } from "../../../batch/types";
import { isDuplicatedCommission } from "../../../utils";
import { DEFAULT_POSTPONED_COMMISSIONS_QUERY_PARAMS } from "../../api";
import { PostponedCommissionsOrderByColumn, PostponedCommissionsReport } from "../../enums";
import { PostponedCommissionsFilterPageRequest, PostponedCommissionsFilterPageResult } from "../../types";

type ActionButtonProps = {
  selectedPostponedCommissions: Commission[];
  setSelectedPostponedCommissions: (selectedPostponedCommissions: Commission[]) => void;
  report?: PostponedCommissionsReport;
};

const ActionButton = ({ report, selectedPostponedCommissions, setSelectedPostponedCommissions }: ActionButtonProps) => {
  const { t } = useTranslation();

  const resolvePostponedCommission = useResolvePostponedCommission();
  const unresolvePostponedCommission = useUnresolvePostponedCommission();

  switch (report) {
    case PostponedCommissionsReport.UNRESOLVED:
      return (
        <Popconfirm
          title={t("commissions.postponed.helpers.resolveConfirmPlural")}
          icon={<AntIcon type="question" />}
          okText={t("common.yes")}
          cancelText={t("common.no")}
          okType="primary"
          onConfirm={() => {
            resolvePostponedCommission.mutate({
              entries: selectedPostponedCommissions.map((commission) => ({
                id: commission.id,
                optimisticLockVersion: commission.optimisticLockVersion,
              })),
            });

            setSelectedPostponedCommissions([]);
          }}
        >
          <Button
            size="small"
            color="orange"
            variant="outlined"
            icon={<AntIcon type="check-circle" />}
            disabled={!selectedPostponedCommissions.length}
          >
            {t("commissions.postponed.actions.resolved")}
          </Button>
        </Popconfirm>
      );
    case PostponedCommissionsReport.RESOLVED:
      return (
        <Popconfirm
          title={t("commissions.postponed.helpers.unresolveConfirmPlural")}
          icon={<AntIcon type="question" />}
          okText={t("common.yes")}
          cancelText={t("common.no")}
          okType="primary"
          onConfirm={() => {
            unresolvePostponedCommission.mutate({
              entries: selectedPostponedCommissions.map((commission) => ({
                id: commission.id,
                optimisticLockVersion: commission.optimisticLockVersion,
              })),
            });

            setSelectedPostponedCommissions([]);
          }}
        >
          <Button
            size="small"
            color="blue"
            variant="outlined"
            icon={<AntIcon type="reload" />}
            disabled={!selectedPostponedCommissions.length}
          >
            {t("commissions.postponed.actions.unresolved")}
          </Button>
        </Popconfirm>
      );
  }

  return null;
};

interface Props {
  searchParams: PostponedCommissionsFilterPageRequest;
}

export const PostponedCommissionsTableView = ({ searchParams }: Props) => {
  const { data, isPending } = useFilterPostponedCommissions(searchParams);

  return (
    // Remounted when the report changes, so that nothing selected in the previous report stays selected
    <PostponedCommissionsTableContent
      key={data?.report}
      searchParams={searchParams}
      postponedCommissions={data}
      isPending={isPending}
    />
  );
};

type ContentProps = Props & {
  postponedCommissions?: PostponedCommissionsFilterPageResult;
  isPending: boolean;
};

const PostponedCommissionsTableContent = ({ searchParams, postponedCommissions, isPending }: ContentProps) => {
  const { t } = useTranslation();

  const [commissionToUpdate, setCommissionToUpdate] = useState<Commission>();

  const hasManagePermission = useHasPermissions(Permission.POSTPONED_COMMISSIONS_MANAGE);

  const hasReadContractPermission = useHasAnyPermission(
    Permission.INSURANCE_READ,
    Permission.LOAN_READ,
    Permission.INVESTMENT_READ,
    Permission.DEPOSIT_READ,
    Permission.SECOND_PILLAR_READ,
    Permission.THIRD_PILLAR_READ,
    Permission.GENERIC_READ,
  );

  const [selectedPostponedCommissions, setSelectedPostponedCommissions] = useState<Commission[]>([]);

  const resolvePostponedCommission = useResolvePostponedCommission();
  const unresolvePostponedCommission = useUnresolvePostponedCommission();
  const downloadPostponedCommissionsExport = useDownloadPostponedCommissionsExport();

  const updateSearchParams = useUpdateFilterSearchParams<PostponedCommissionsFilterPageRequest>(
    DEFAULT_POSTPONED_COMMISSIONS_QUERY_PARAMS,
  );

  const actionsColumnWidth = postponedCommissions?.report === PostponedCommissionsReport.UNRESOLVED ? 280 : 200;

  const getColumns = () => {
    const columns: ColumnsType<Commission> = [];

    if (postponedCommissions?.report !== PostponedCommissionsReport.UNRESOLVED) {
      columns.push({
        key: "duplicateOfBatchName",
        width: 200,
        title: t("commissions.postponed.helpers.originBatch"),
        ...sortableColumnProps(PostponedCommissionsOrderByColumn.BATCH_NAME, postponedCommissions),
        render: (_, record) =>
          isDuplicatedCommission(record) && (
            <Ellipsis>
              <Link
                to={generatePath(COMMISSIONS_BATCH_ROUTE_PATHS.detail.to, { id: record.duplicateOf.batch.id })}
                target="_blank"
              >
                {record.duplicateOf.batch.name}
              </Link>
            </Ellipsis>
          ),
      });
    }

    columns.push(
      {
        key: "batchName",
        title:
          postponedCommissions?.report === PostponedCommissionsReport.UNRESOLVED
            ? t("commissions.postponed.helpers.originBatch")
            : postponedCommissions?.report === PostponedCommissionsReport.UNRESOLVED_INCLUDED_IN_BATCH
              ? t("commissions.postponed.helpers.includedInBatch")
              : t("commissions.postponed.helpers.resolvedInBatch"),
        width: 200,
        ...sortableColumnProps(
          postponedCommissions?.report === PostponedCommissionsReport.UNRESOLVED
            ? PostponedCommissionsOrderByColumn.BATCH_NAME
            : PostponedCommissionsOrderByColumn.DUPLICATE_OF_BATCH_NAME,
          postponedCommissions,
        ),
        render: (_, record) => (
          <Ellipsis>
            <Link to={generatePath(COMMISSIONS_BATCH_ROUTE_PATHS.detail.to, { id: record.batch.id })} target="_blank">
              {record.batch.name}
            </Link>
          </Ellipsis>
        ),
      },
      {
        key: "institution",
        title: t("commissions.batch.attrs.commission.institution"),
        width: 100,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{record.institution.name}</Ellipsis>,
      },
      {
        key: "contract",
        title: t("commissions.batch.attrs.commission.contract"),
        width: 110,
        ellipsis: { showTitle: false },
        render: (_, record) =>
          record.contract && hasReadContractPermission ? (
            <>
              {record.contractNumber && (
                <>
                  <Ellipsis tooltip={record.contractNumber}>
                    <Link to={generatePath(CONTRACT_ROUTE_PATHS.detail.to, { id: record.contract.id })} target="_blank">
                      {record.contractNumber}
                    </Link>
                  </Ellipsis>
                  <br />
                </>
              )}
              {record.secondaryContractNumber && (
                <Ellipsis tooltip={record.secondaryContractNumber}>
                  <Link to={generatePath(CONTRACT_ROUTE_PATHS.detail.to, { id: record.contract.id })} target="_blank">
                    {record.secondaryContractNumber}
                  </Link>
                </Ellipsis>
              )}
            </>
          ) : (
            <>
              {record.contractNumber && (
                <>
                  <Ellipsis>{record.contractNumber}</Ellipsis>
                  <br />
                </>
              )}
              {record.secondaryContractNumber && <Ellipsis>{record.secondaryContractNumber}</Ellipsis>}
            </>
          ),
      },
      {
        key: "product",
        title: t("commissions.batch.attrs.commission.product"),
        width: 150,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{record.contract?.product.name}</Ellipsis>,
      },
      {
        key: "tariffCode",
        title: t("commissions.batch.attrs.commission.tariffCode"),
        width: 90,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{record.tariffCode}</Ellipsis>,
      },
      {
        key: "clientName",
        title: t("commissions.batch.attrs.commission.clientName"),
        width: 140,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{record.clientName}</Ellipsis>,
      },
      {
        key: "dates",
        title: t("commissions.batch.attrs.commission.dates"),
        width: 90,
        render: (_, record) => (
          <>
            {record.startDate ? formatLocaleDate(record.startDate) : undefined}
            <br />
            {record.endDate ? formatLocaleDate(record.endDate) : undefined}
          </>
        ),
      },
      {
        key: "commissionKind",
        title: t("commissions.batch.enums.commissionKind._label"),
        align: "center",
        width: 190,
        render: (_, record) => (record.commissionKind ? <CommissionKindTag kind={record.commissionKind} /> : undefined),
      },
      {
        key: "commissionBaseAmount",
        title: t("commissions.batch.attrs.commission.commissionBaseAmount"),
        align: "right",
        width: 140,
        ...sortableColumnProps(PostponedCommissionsOrderByColumn.COMMISSION_BASE_AMOUNT, postponedCommissions),
        render: (_, record) =>
          isDefined(record.commissionBaseAmount) ? formatLocaleCurrency(record.commissionBaseAmount) : undefined,
      },
      {
        key: "commissionAmount",
        title: t("commissions.batch.attrs.commission.commissionAmount"),
        align: "right",
        width: 110,
        ...sortableColumnProps(PostponedCommissionsOrderByColumn.COMMISSION_AMOUNT, postponedCommissions),
        render: (_, record) =>
          isDefined(record.commissionAmount) ? formatLocaleCurrency(record.commissionAmount) : undefined,
      },
      {
        key: "postponementReason",
        title: t("commissions.postponed.helpers.originalPostponementReason"),
        align: "center",
        width: 170,
        render: (_, record) =>
          isDuplicatedCommission(record) ? (
            <PostponementReasonTag
              reason={record.duplicateOf.postponementReason}
              manualPostponementReason={record.duplicateOf.manualPostponementReason}
            />
          ) : (
            <PostponementReasonTag
              reason={record.postponementReason}
              manualPostponementReason={record.manualPostponementReason}
            />
          ),
      },
    );

    if (hasManagePermission) {
      columns.push({
        key: "actions",
        align: "right",
        fixed: "right",
        width: actionsColumnWidth,
        render: (_, record) =>
          postponedCommissions?.report === PostponedCommissionsReport.UNRESOLVED ? (
            <>
              <ActionTextIcon
                icon="edit"
                color="blue"
                text={t("common.edit")}
                onClick={() => setCommissionToUpdate(record)}
              />

              <Divider orientation="vertical" />

              <Popconfirm
                title={t("commissions.postponed.helpers.resolveConfirm")}
                icon={<AntIcon type="question" />}
                okText={t("common.yes")}
                cancelText={t("common.no")}
                okType="primary"
                onConfirm={() =>
                  resolvePostponedCommission.mutate({
                    entries: [{ id: record.id, optimisticLockVersion: record.optimisticLockVersion }],
                  })
                }
              >
                <ActionTextIcon icon="check-circle" color="orange" text={t("commissions.postponed.actions.resolve")} />
              </Popconfirm>
            </>
          ) : (
            <Popconfirm
              title={t("commissions.postponed.helpers.unresolveConfirm")}
              icon={<AntIcon type="question" />}
              okText={t("common.yes")}
              cancelText={t("common.no")}
              okType="primary"
              onConfirm={() =>
                unresolvePostponedCommission.mutate({
                  entries: [{ id: record.id, optimisticLockVersion: record.optimisticLockVersion }],
                })
              }
              disabled={!record.manuallyResolvedAfterPostponement}
            >
              <ActionTextIcon
                icon="reload"
                color="blue"
                text={t("commissions.postponed.actions.unresolve")}
                disabled={!record.manuallyResolvedAfterPostponement}
              />
            </Popconfirm>
          ),
      });
    }

    return columns;
  };

  return (
    <Card className="card-box">
      <Space size="large" className="margin-bottom-small">
        <Space>
          <b>{t("commissions.batch.helpers.filteredCommissionsSum")}:</b>
          <span>{formatLocaleCurrency(postponedCommissions?.commissionAmountsSum ?? 0)}</span>
        </Space>

        <div>
          <div>
            <ActionTextIcon
              icon="download"
              color="blue"
              text={t("common.exportXlsx")}
              onClick={() =>
                downloadPostponedCommissionsExport.mutate({ ...searchParams, exportFileType: ExportFileType.XLSX })
              }
            />

            <Divider orientation="vertical" />

            <ActionTextIcon
              icon="download"
              color="green"
              text={t("common.exportCsv")}
              onClick={() =>
                downloadPostponedCommissionsExport.mutate({ ...searchParams, exportFileType: ExportFileType.CSV })
              }
            />
          </div>
        </div>
      </Space>

      <div className={postponedCommissions?.pageData.length ? "table-header-margin" : "margin-bottom-small"}>
        {hasManagePermission && postponedCommissions?.pageData.length ? (
          <ActionButton
            selectedPostponedCommissions={selectedPostponedCommissions}
            setSelectedPostponedCommissions={setSelectedPostponedCommissions}
            report={postponedCommissions?.report}
          />
        ) : undefined}
      </div>

      <Table<Commission>
        {...tableStandardProps()}
        columns={getColumns()}
        dataSource={postponedCommissions?.pageData}
        scroll={{ x: TableSizes.XXL }}
        loading={isPending}
        rowSelection={
          postponedCommissions?.report !== PostponedCommissionsReport.UNRESOLVED_INCLUDED_IN_BATCH &&
          hasManagePermission
            ? {
                onChange: (_, selectedRows) => setSelectedPostponedCommissions(selectedRows),
                getCheckboxProps: (record) => ({
                  disabled:
                    !record.manuallyResolvedAfterPostponement &&
                    postponedCommissions?.report === PostponedCommissionsReport.RESOLVED,
                }),
                selectedRowKeys: selectedPostponedCommissions.map((commission) => commission.id),
                columnWidth: 40,
              }
            : undefined
        }
        expandable={{
          columnWidth: 15,
          rowExpandable: (record) =>
            !!record.errors.length &&
            postponedCommissions?.report !== PostponedCommissionsReport.UNRESOLVED_INCLUDED_IN_BATCH,
          expandedRowRender: (record) => (
            <Row gutter={rowGutter} className="margin-top-small margin-bottom-small">
              <Col flex="70px">
                <b>{t("common.errors")}:</b>
              </Col>

              <Col flex="auto">{record.errors.map((error) => error.description).join(", ")}</Col>
            </Row>
          ),
        }}
        pagination={{
          ...paginationTableProps,
          current: numberOrZero(postponedCommissions?.pageIndex) + 1,
          pageSize: postponedCommissions?.pageSize,
          total: postponedCommissions?.totalElementsCount,
        }}
        onChange={(pagination, __, sorter) => {
          const { orderBy, orderDirections } = getTableSorting<PostponedCommissionsOrderByColumn>(sorter);

          updateSearchParams(
            { orderBy, orderDirections, pageIndex: pagination.current ? pagination.current - 1 : undefined },
            true,
            false,
          );
        }}
      />

      <CommissionForm
        open={!!commissionToUpdate}
        formType="postponedCommission"
        commission={commissionToUpdate}
        onFormCancel={() => setCommissionToUpdate(undefined)}
      />
    </Card>
  );
};
